<script>
    import {onMount} from 'svelte';
    import {browser} from '$app/environment';
    import {beforeNavigate, afterNavigate, invalidate} from '$app/navigation';

    import {start_date_string, end_date_string, meetings, meetings_total, clicked_date, agenda_feed_scroll_position} from '$lib/store.js';
    import {clear_clicked_cat_and_date} from '$lib/utils.js';

    import Head from '$lib/Head.svelte';
    import Search from '../Search.svelte';
    import CreateDossierButton from '../CreateDossierButton.svelte';
    import Meeting from '../Meeting.svelte';
    import TryPro from '../TryPro.svelte';
    import GetHelp from '../GetHelp.svelte';
    import Dossiers from '../Dossiers.svelte';
    import DateFilter from '../DateFilter.svelte';
    import filter_icon from '../filter.svg';

    export let data;

    // Stores the width of the window
    let innerWidth = 0;
    let offcanvas;
    let opened_canvas;
    $: {
        // If the filters offcanvas is open (on small screens), close it when
        // the window is resized to lg or larger
        if (innerWidth > 991) {
            opened_canvas = bootstrap.Offcanvas.getInstance(offcanvas);
            if (opened_canvas) {
                opened_canvas.hide();
            }
        }
    }

    // Rerun the +page.js load function when the user enters/changes a date
    // (clicking already worked, but this is needed for manually
    // entered/typed dates)
    $: {
        $start_date_string;
        $end_date_string;
        process_date();
    }

    // These lines are in a separate function otherwise changes of
    // $clicked_date would trigger the reactivity above
    function process_date() {
        // Clear any previous 'clicked_*' states
        clear_clicked_cat_and_date();
        $clicked_date = true;
        // Check if the event element has a parent with the class
        // 'date-time-picker' which means that the user selected a new date
        // using the picker or 'date-time-field' which means the input field
        // was changed directly by the user (i.e typing/clearing the input
        // field)
        if (browser && event?.srcElement.closest('.date-time-picker, .date-time-field')) {
            // Rerun the load function
            invalidate('app:agenda:page');
        }
    }

	onMount(() => {
        offcanvas = document.getElementById('offcanvas');
    });

    // When returning to the feed
    afterNavigate(({to}) => {
        // Scroll to the previous scrollY position (if not available it
        // defaults to 0); this works both with the back button and 'Meer
        // documenten' button
        window.scrollTo(0, $agenda_feed_scroll_position);
    });

    // When navigating away from the feed
    beforeNavigate(() => {
        // Save the current scrollY position
        $agenda_feed_scroll_position = window.scrollY;

        // Clear any previous 'clicked_*' states
        clear_clicked_cat_and_date();
    });
</script>

<svelte:window bind:innerWidth/>

<Head title="Politieke monitor en zoekmachine voor Binnenhof en EU | 1848.nl"/>

<h1 class="visually-hidden">Agenda feed</h1>
<div class="col-lg-7 col-xxl-8 order-2 order-lg-1">
    <h2 class="visually-hidden">Feed</h2>
    <Search location='/app/agenda'/>

    <button id="filter-button" class="d-lg-none btn btn-medium bg-gold" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvas" aria-controls="offcanvas">
        Filter <img style="margin-left:10px;" src={filter_icon} alt="" width="25" height="22.4333">
    </button>

    <CreateDossierButton display='d-lg-none' --margin='0 0 10px 0'/>

    {#if data.logged_in}
        <Dossiers display='d-lg-none' show='' location='/app/agenda'/>
    {/if}

    <div id="feed">
        {#if $meetings}
            {#each $meetings as meeting, i}
                {#if !data.logged_in}
                    {#if i == 1}
                        <TryPro display='d-lg-none' --margin_bottom={'30px'}/>
                    {/if}

                    {#if i == 2}
                        <GetHelp display='d-lg-none' --margin_bottom={'30px'}/>
                    {/if}
                {/if}

                <div id={meeting.doc_type + '_' + meeting.doc_id} class="bg-white feed-agenda">
                    <Meeting {meeting}/>
                </div>
            {/each}
        {/if}
    </div>

    {#if !$meetings_total}
        <p class="text-center">Deze zoekopdracht leverde geen resultaten op.</p>
    {/if}
</div>

<aside class="d-none d-lg-block offset-lg-1 col-lg-4 col-xxl-3 order-1 order-lg-2">
    {#if data.logged_in}
        <Dossiers show='show' allow_modals=true location='/app/agenda'/>
    {:else}
        <TryPro --margin_bottom={'20px'}/>
        <GetHelp --margin_bottom={'37px'}/>
    {/if}

    <h2 class="visually-hidden">Filters</h2>
    <DateFilter/>
</aside>

<div class="offcanvas offcanvas-start" tabindex="-1" id="offcanvas" aria-labelledby="offcanvas-label">
    <div class="offcanvas-header justify-content-end">
        <h2 id="offcanvas-label" class="visually-hidden">Filters</h2>
        <button type="button" class="btn-close text-end" data-bs-dismiss="offcanvas" aria-label="Sluiten"></button>
    </div>
    <div class="offcanvas-body">
        <DateFilter/>
    </div>
</div>

<style lang="scss">
    #feed {
        margin-top: 20px;
    }

    .feed-agenda {
        padding: 50px 57px 44px 30px;
        margin-bottom: 30px;
        word-wrap: break-word;

        &:last-child {
            margin-bottom: 0;
        }
    }

    #filter-button {
        margin-bottom: 10px;
        padding: 8px 14px 8.5px 22px;
        line-height: 22px;
        border-radius: 100px;
        border-width: 0;
        box-shadow: none;
        font-weight: bold;
    }

    @media (max-width: 575px) {
        .feed-agenda {
            padding: 20px;
        }
    }
</style>
